import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Loader2, Sparkles, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Google OAuth callback — receives ?code=...&state=... from Google's
// redirect, exchanges for our JWT, then sends the user to their
// destination. The original implementation race-condition'd because
// the React Navigate fired BEFORE AuthContext.user propagated, so a
// ProtectedRoute would briefly bounce the user back to /login.
//
// Fix: don't navigate until BOTH:
//   1. The token-exchange completes (token + user written)
//   2. AuthContext.user has actually flipped to truthy
// We render <Navigate /> declaratively so React Router commits the
// transition during its own render cycle — no setTimeout-based race
// (timers behave poorly in headless test environments anyway).
const STAGE_LABELS = [
  'Verifying your Google identity…',
  'Securing your session…',
  "Loading your tutor's memory…",
  'Preparing your dashboard…',
  'Welcome back.',
];

const GoogleOAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const { setGoogleUser, user } = useAuth();

  // `target` flipping from null → {path, state} is what triggers
  // <Navigate /> to render. Once set, we never unset.
  const [target, setTarget] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [stage, setStage] = useState(0);

  // Drive the 5-stage visual via setInterval — 1Hz survives the
  // throttling that headless browsers apply to setTimeout.
  useEffect(() => {
    const interval = setInterval(() => {
      setStage((s) => (s < STAGE_LABELS.length ? s + 1 : s));
    }, 1600);
    return () => clearInterval(interval);
  }, []);

  // Token exchange — runs once, dedupes across StrictMode double-mount
  // by pinning the in-flight promise to window.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const code = searchParams.get('code');
        const oauthError = searchParams.get('error');
        if (oauthError) {
          if (!cancelled) {
            setErrorMsg('Google sign-in was cancelled.');
            setTarget({ path: '/login', state: { error: 'Google sign-in was cancelled.' } });
          }
          return;
        }
        if (!code) {
          if (!cancelled) {
            setErrorMsg('No authorization code received from Google.');
            setTarget({ path: '/login', state: { error: 'No authorization code received from Google.' } });
          }
          return;
        }
        if (!window.__istudypro_oauth_promise || window.__istudypro_oauth_code !== code) {
          window.__istudypro_oauth_code = code;
          window.__istudypro_oauth_promise = axios.post(
            `${API}/api/auth/google/token-exchange`,
            { code }
          );
        }
        const res = await window.__istudypro_oauth_promise;
        const { access_token, user: u, is_new_user } = res.data;
        if (access_token) {
          localStorage.setItem('auth_token', access_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        }
        if (u) setGoogleUser(u);
        if (!cancelled) {
          setTarget({
            path: is_new_user ? '/onboarding' : '/app',
            state: { user: u },
          });
        }
      } catch (err) {
        const msg = err.response?.data?.detail || 'Google authentication failed. Please try again.';
        if (!cancelled) {
          setErrorMsg(msg);
          setTarget({ path: '/login', state: { error: msg } });
        }
      }
    };
    run();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render-time navigation. We only commit once the target is set AND
  // (for the happy path) the AuthContext.user has flipped. For error
  // paths we navigate immediately to /login regardless of user state.
  // We intentionally skip the "wait for 5-stage ritual" gate here — in
  // headless test envs the setInterval gets throttled and the gate
  // would hang the redirect indefinitely. Real users get the visual
  // beat anyway because the network round-trip + AuthContext propagate
  // typically takes ~1-2s; if they want a longer dwell we can switch
  // to a CSS-animation-driven gate later.
  if (target) {
    const isErrorPath = target.path === '/login';
    const userReady = !!user || target.path === '/onboarding';
    if (isErrorPath || userReady) {
      return <Navigate to={target.path} state={target.state} replace />;
    }
  }

  const isComplete = stage >= STAGE_LABELS.length;
  const labelIdx = Math.min(stage, STAGE_LABELS.length - 1);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ backgroundColor: 'var(--isp-page)' }}
      data-testid="google-oauth-callback"
    >
      <div className="text-center max-w-md w-full">
        <div className="relative mb-8 inline-block">
          {isComplete && !errorMsg ? (
            <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-9 h-9 text-green-500" />
            </div>
          ) : (
            <>
              <Loader2 className="w-16 h-16 text-[#002FA7] animate-spin mx-auto" />
              <Sparkles className="w-6 h-6 text-[#FFB800] absolute -top-1 -right-1 animate-pulse" />
            </>
          )}
        </div>

        <h2 className="text-2xl font-semibold tracking-tight text-[var(--isp-heading)] mb-3">
          {errorMsg ? 'Sign-in failed' : 'Signing you in'}
        </h2>

        <p
          className="text-[var(--isp-muted)] text-sm min-h-[1.5rem]"
          aria-live="polite"
          data-testid="oauth-stage-label"
        >
          {errorMsg || STAGE_LABELS[labelIdx]}
        </p>

        <div className="mt-7 flex items-center justify-center gap-1.5">
          {STAGE_LABELS.map((_, i) => (
            <div
              key={i}
              className={`h-1 w-10 rounded-full transition-colors duration-500 ${
                i < stage ? 'bg-[#002FA7]' : 'bg-black/10'
              }`}
              data-testid={`oauth-progress-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default GoogleOAuthCallback;
